import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { InsightDto } from '@ecom/contracts';
import { useUiStore } from '@/i18n';
import type { MessageKey } from '@/i18n/messages';
import { useT, useStatusLabel } from '@/i18n/use-t';
import { count, money } from '@/lib/format';
import { halalasToRiyals, riyalsToHalalas } from '@/lib/price-input';
import { useAuthStore } from '@/stores/auth.store';
import { useInsightsStore } from '@/stores/insights.store';
import { Section } from './page';
import { Button, Input, Pill, Skeleton, cx } from './ui';

/**
 * The overview's suggestions, monthly goal and live view (ADR-121).
 */

const SEVERITY_TONE = { high: 'danger', medium: 'warning', low: 'neutral' } as const;

/** A figure a suggestion carries, worded: amounts in halalas, shares as fractions. */
function useInsightValues() {
  const locale = useUiStore((state) => state.locale);
  return (insight: InsightDto): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(insight.values)) {
      if (typeof value === 'string') out[key] = value;
      else if (['value', 'given', 'profit', 'atCost', 'atPrice', 'projected', 'target', 'neededPerDay'].includes(key)) out[key] = money(value, locale);
      else if (['share', 'now', 'before'].includes(key)) out[key] = new Intl.NumberFormat(locale === 'ar' ? 'ar-SA' : 'en-SA', { style: 'percent', maximumFractionDigits: 1 }).format(value);
      else out[key] = count(value, locale);
    }
    return out;
  };
}

export function InsightsSection() {
  const { insights, loadInsights } = useInsightsStore();
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const words = useInsightValues();

  useEffect(() => {
    void loadInsights();
  }, [loadInsights, locale]);

  return (
    <Section title={t('insightsTitle')} subtitle={t('insightsHint')}>
      {insights === null ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-12" />
          ))}
        </div>
      ) : insights.length === 0 ? (
        <p className="py-4 text-sm text-ink-muted">{t('insightsNone')}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line">
          {insights.map((insight) => {
            const query = insight.action.query ? `?${new URLSearchParams(insight.action.query).toString()}` : '';
            return (
              <li key={insight.kind} className="flex items-start gap-3 py-3">
                <Pill tone={SEVERITY_TONE[insight.severity]}>{t(`insightSeverity_${insight.severity}` as MessageKey)}</Pill>
                <p className="min-w-0 flex-1 text-sm text-ink">{t(`insight_${insight.kind}` as MessageKey, words(insight))}</p>
                <Link to={`${insight.action.path}${query}`} className="shrink-0 text-sm font-medium text-brand-600 hover:underline">
                  {t(`insightAction_${insight.kind}` as MessageKey)}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

export function TargetSection() {
  const { target, loadTarget, saveTarget } = useInsightsStore();
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const owner = useAuthStore((state) => state.user?.role === 'OWNER');
  const [editing, setEditing] = useState(false);
  const [revenue, setRevenue] = useState('');
  const [orders, setOrders] = useState('');

  useEffect(() => {
    void loadTarget();
  }, [loadTarget]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const ok = await saveTarget({
      monthlyRevenue: revenue.trim() ? riyalsToHalalas(revenue) : null,
      monthlyOrders: /^\d+$/.test(orders.trim()) ? Number(orders.trim()) : null,
    });
    if (ok) setEditing(false);
  }

  if (!target) return <Section title={t('targetTitle')}>{<Skeleton className="h-24" />}</Section>;
  const hasGoal = target.revenue.target !== null || target.orders.target !== null;
  const bar = (actual: number, projected: number, goal: number | null) => {
    if (goal === null || goal <= 0) return null;
    const done = Math.min(100, (actual / goal) * 100);
    const pace = Math.min(100, (projected / goal) * 100);
    const behind = projected < goal;
    return (
      <span className="relative block h-2 overflow-hidden rounded-full bg-surface-sunken">
        <span className={cx('absolute inset-y-0 start-0 opacity-30', behind ? 'bg-danger' : 'bg-success')} style={{ width: `${pace}%` }} />
        <span className={cx('absolute inset-y-0 start-0', behind ? 'bg-warning' : 'bg-success')} style={{ width: `${done}%` }} />
      </span>
    );
  };

  return (
    <Section
      title={t('targetTitle')}
      subtitle={t('targetDay', { day: count(target.day, locale), days: count(target.days, locale) })}
      action={owner && !editing ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setRevenue(target.revenue.target !== null ? halalasToRiyals(target.revenue.target) : '');
            setOrders(target.orders.target !== null ? String(target.orders.target) : '');
            setEditing(true);
          }}
        >
          {hasGoal ? t('targetChange') : t('targetSet')}
        </Button>
      ) : undefined}
    >
      {editing ? (
        <form onSubmit={submit} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-xs text-ink-muted">
            {t('targetRevenue')}
            <Input dir="ltr" inputMode="decimal" value={revenue} onChange={(event) => setRevenue(event.target.value)} placeholder="150000" />
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-muted">
            {t('targetOrders')}
            <Input dir="ltr" inputMode="numeric" value={orders} onChange={(event) => setOrders(event.target.value)} placeholder="450" />
          </label>
          <div className="flex gap-2">
            <Button type="submit" size="sm">
              {t('save')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              {t('cancel')}
            </Button>
          </div>
        </form>
      ) : !hasGoal ? (
        <p className="text-sm text-ink-muted">{owner ? t('targetNoneOwner') : t('targetNone')}</p>
      ) : (
        <div className="flex flex-col gap-4">
          {target.revenue.target !== null ? (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="text-ink">{t('targetRevenue')}</span>
                <span data-numeric className="font-medium text-ink">
                  {money(target.revenue.actual, locale)} / {money(target.revenue.target, locale)}
                </span>
              </div>
              {bar(target.revenue.actual, target.revenue.projected, target.revenue.target)}
              <span className="text-xs text-ink-muted">
                {t('targetPace', { projected: money(target.revenue.projected, locale), needed: money(target.revenue.neededPerDay ?? 0, locale) })}
              </span>
            </div>
          ) : null}
          {target.orders.target !== null ? (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="text-ink">{t('targetOrders')}</span>
                <span data-numeric className="font-medium text-ink">
                  {count(target.orders.actual, locale)} / {count(target.orders.target, locale)}
                </span>
              </div>
              {bar(target.orders.actual, target.orders.projected, target.orders.target)}
              <span className="text-xs text-ink-muted">
                {t('targetPace', { projected: count(target.orders.projected, locale), needed: count(target.orders.neededPerDay ?? 0, locale) })}
              </span>
            </div>
          ) : null}
        </div>
      )}
    </Section>
  );
}

/** Refreshed every 15 seconds while the overview is open. */
const LIVE_EVERY_MS = 15_000;

export function LiveSection() {
  const { live, loadLive } = useInsightsStore();
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const statusLabel = useStatusLabel();

  useEffect(() => {
    void loadLive();
    const timer = window.setInterval(() => {
      // A tab in the background need not ask.
      if (document.visibilityState === 'visible') void loadLive();
    }, LIVE_EVERY_MS);
    return () => window.clearInterval(timer);
  }, [loadLive]);

  if (!live) return <Section title={t('liveTitle')}>{<Skeleton className="h-28" />}</Section>;
  const figure = (label: string, value: string, hint?: string) => (
    <div className="flex flex-col gap-0.5 rounded-control bg-surface-sunken px-4 py-3">
      <span className="text-xs text-ink-muted">{label}</span>
      <span data-numeric className="text-xl font-bold text-ink">
        {value}
      </span>
      {hint ? <span className="text-xs text-ink-faint">{hint}</span> : null}
    </div>
  );

  return (
    <Section
      title={
        <span className="flex items-center gap-2">
          <span className="relative flex size-2">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-60" />
            <span className="relative inline-flex size-2 rounded-full bg-success" />
          </span>
          {t('liveTitle')}
        </span>
      }
      subtitle={t('liveHint')}
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
          {figure(t('liveVisitors'), count(live.visitors, locale))}
          {figure(t('liveInCart'), count(live.inCart, locale))}
          {figure(t('liveAtCheckout'), count(live.atCheckout, locale))}
          {figure(t('liveOrdersToday'), count(live.today.orders, locale), t('liveYesterday', { value: count(live.yesterday.orders, locale) }))}
          {figure(t('liveRevenueToday'), money(live.today.revenue, locale), t('liveYesterday', { value: money(live.yesterday.revenue, locale) }))}
          {figure(t('liveVisitsToday'), count(live.today.visits, locale), t('liveYesterday', { value: count(live.yesterday.visits, locale) }))}
        </div>
        <div className="grid gap-4 lg:grid-cols-[1fr_2fr]">
          <div className="flex flex-col gap-2 text-sm">
            <span className="text-xs font-medium text-ink-muted">{t('liveBySource')}</span>
            {live.bySource.length === 0 ? <span className="text-ink-faint">—</span> : null}
            {live.bySource.slice(0, 5).map((row) => (
              <div key={row.channel} className="flex justify-between gap-2">
                <span className="text-ink">{t(`channel_${row.channel}` as MessageKey)}</span>
                <span data-numeric className="text-ink-muted">{count(row.visitors, locale)}</span>
              </div>
            ))}
            {live.byDevice.length > 0 ? (
              <span className="mt-1 text-xs text-ink-faint">
                {live.byDevice.map((row) => `${t(`liveDevice_${row.device}` as MessageKey)} ${count(row.visitors, locale)}`).join(' · ')}
              </span>
            ) : null}
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <span className="text-xs font-medium text-ink-muted">{t('liveLatestOrders')}</span>
            {live.latestOrders.map((order) => (
              <Link key={order.id} to={`/orders/${order.id}`} className="flex items-center justify-between gap-3 rounded-control px-2 py-1 hover:bg-surface-sunken">
                <span className="flex min-w-0 items-center gap-2">
                  <span dir="ltr" className="font-mono text-xs text-ink">{order.number}</span>
                  <span className="truncate text-ink-muted">{order.city ?? ''}</span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="text-xs text-ink-faint">{statusLabel(order.status)}</span>
                  <span data-numeric className="font-medium text-ink">{money(order.total, locale)}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}
